import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/use-user-role";
import { AdminCoachList } from "@/components/AdminCoachList";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, User as UserIcon, ClipboardList, BarChart3, Menu } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/logo.svg";

const AdminPortal = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      toast.error("Access denied. This page is for admins only.");
      navigate("/");
    }
  }, [isAdmin, roleLoading, navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-3">
            <img src={logo} alt="Vocalzing logo" className="h-8 w-8 md:h-10 md:w-10" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold">Admin Portal</h1>
              <p className="text-xs md:text-sm text-muted-foreground hidden sm:block">Manage coaches and system settings</p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-2">
            <Button onClick={() => navigate("/profile")} variant="ghost" size="sm">
              <UserIcon className="mr-2 h-4 w-4" />
              Profile
            </Button>
            <Button onClick={handleSignOut} variant="outline" size="sm">
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>

          {/* Mobile Hamburger Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="sm">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <div className="flex flex-col gap-4 mt-8">
                <Button 
                  onClick={() => {
                    navigate("/profile");
                    setMobileMenuOpen(false);
                  }} 
                  variant="ghost" 
                  className="w-full justify-start"
                >
                  <UserIcon className="h-4 w-4 mr-2" />
                  Profile
                </Button>
                <Button 
                  onClick={() => {
                    handleSignOut();
                    setMobileMenuOpen(false);
                  }} 
                  variant="outline"
                  className="w-full justify-start"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex gap-3">
          <Button onClick={() => navigate("/admin/analytics")} variant="outline" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            View Analytics
          </Button>
          <Button onClick={() => navigate("/admin/coach-applications")} variant="outline" className="gap-2">
            <ClipboardList className="w-4 h-4" />
            View Coach Applications
          </Button>
        </div>
        <AdminCoachList />
      </main>
    </div>
  );
};

export default AdminPortal;
